import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { toPng } from "html-to-image";
import { useDocumentTitle } from "../../../../utils";
import LandmarkLogo from "./LandmarkLogo";
import { TEMPLATES, TEMPLATE_BY_ID } from "./templates";
import { CANVAS_SIZE, DEFAULT_DATA, DEFAULT_HEADSHOT } from "./templates/shared";
import "./WelcomePost.scss";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Inter:wght@400;500;600;700;800&family=Caveat:wght@600&display=swap";

const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;

// The app shell forces the template's dark theme (body[data-theme-version="dark"]),
// whose global overrides fight this tool's own light styling. Force light while
// mounted and restore the previous value on unmount.
function useLightTheme() {
  useEffect(() => {
    const body = document.body;
    const previous = body.getAttribute("data-theme-version");
    body.setAttribute("data-theme-version", "light");
    body.setAttribute("data-welcome-post", "true");
    return () => {
      if (previous) body.setAttribute("data-theme-version", previous);
      else body.removeAttribute("data-theme-version");
      body.removeAttribute("data-welcome-post");
    };
  }, []);
}

// Load the Google fonts the templates use once, and keep the link tag around so
// exports on repeat visits are instant.
function useGoogleFonts() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

// Renders a native 1080x1080 canvas scaled down to fill its container's width.
function ScaledCanvas({ children, className = "", canvasRef, ...rest }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.3);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setScale(el.clientWidth / CANVAS_SIZE);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className={`lw-scaled ${className}`} {...rest}>
      <div className="lw-scaled__inner" style={{ transform: `scale(${scale})` }}>
        <div ref={canvasRef}>{children}</div>
      </div>
    </div>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function slug(value) {
  return (value || "new-hire")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function triggerDownload(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function exportNode(node) {
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  return toPng(node, {
    width: CANVAS_SIZE,
    height: CANVAS_SIZE,
    pixelRatio: 1,
    cacheBust: true,
    style: { transform: "none" },
  });
}

export default function WelcomePost() {
  useDocumentTitle("Welcome Post - Landmark Ventures");
  useLightTheme();
  useGoogleFonts();

  const [data, setData] = useState(DEFAULT_DATA);
  const [headshot, setHeadshot] = useState(DEFAULT_HEADSHOT);
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const [busy, setBusy] = useState(null); // null | "one" | "all"
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const exportRefs = useRef({});

  const template = TEMPLATE_BY_ID[templateId];
  const templateProps = useMemo(() => ({ data, headshot }), [data, headshot]);

  const update = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  // ---- headshot upload -----------------------------------------------------
  const loadFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    try {
      const src = await readFileAsDataUrl(file);
      setHeadshot({ ...DEFAULT_HEADSHOT, src });
    } catch (err) {
      toast.error("Could not read that image");
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    loadFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  // ---- pan (drag anywhere on the preview) ----------------------------------
  const dragState = useRef(null);
  const onPointerDown = (e) => {
    if (!headshot.src || !previewRef.current) return;
    const frame = previewRef.current.querySelector(".wp-headshot");
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: headshot.x,
      baseY: headshot.y,
      w: rect.width,
      h: rect.height,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      /* noop - pointer may already be gone */
    }
  };
  const onPointerMove = (e) => {
    const s = dragState.current;
    if (!s) return;
    const dx = ((e.clientX - s.startX) / s.w) * 100;
    const dy = ((e.clientY - s.startY) / s.h) * 100;
    setHeadshot((h) => ({ ...h, x: clamp(s.baseX + dx, -60, 60), y: clamp(s.baseY + dy, -60, 60) }));
  };
  const onPointerUp = (e) => {
    if (!dragState.current) return;
    dragState.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* noop */
    }
  };

  // ---- export --------------------------------------------------------------
  const baseName = slug(data.name);

  const downloadCurrent = async () => {
    const node = exportRefs.current[templateId];
    if (!node) return;
    setBusy("one");
    try {
      const png = await exportNode(node);
      triggerDownload(png, `welcome-${baseName}-${templateId}.png`);
      toast.success("PNG downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Export failed - check the console");
    } finally {
      setBusy(null);
    }
  };

  const downloadAll = async () => {
    setBusy("all");
    try {
      for (const t of TEMPLATES) {
        const node = exportRefs.current[t.id];
        if (!node) continue;
        // eslint-disable-next-line no-await-in-loop
        const png = await exportNode(node);
        triggerDownload(png, `welcome-${baseName}-${t.id}.png`);
        // Give the browser a beat between downloads so none are dropped.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 350));
      }
      toast.success("All 6 templates downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Export failed - check the console");
    } finally {
      setBusy(null);
    }
  };

  const Active = template.Component;

  return (
    <div className="lw-page">
      <header className="lw-topbar">
        <div className="lw-topbar__brand">
          <Link to="/landmark" className="lw-back" aria-label="Back to Landmark tools">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Link>
          <LandmarkLogo width={132} className="lw-topbar__logo" />
          <span className="lw-topbar__divider" />
          <h1 className="lw-topbar__title">Welcome Post</h1>
        </div>
        <div className="lw-topbar__actions">
          <button type="button" className="lw-btn lw-btn--ghost" onClick={downloadAll} disabled={!!busy}>
            {busy === "all" ? "Exporting…" : "Download all 6"}
          </button>
          <button type="button" className="lw-btn lw-btn--primary" onClick={downloadCurrent} disabled={!!busy}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" />
              <path d="M7 10l5 5 5-5" />
              <path d="M4 21h16" />
            </svg>
            {busy === "one" ? "Exporting…" : "Download PNG"}
          </button>
        </div>
      </header>

      <div className="lw-body">
        <aside className="lw-panel">
          {/* Photo */}
          <section className="lw-section">
            <div className="lw-section__head">
              <h2>Headshot</h2>
              {headshot.src && (
                <button type="button" className="lw-link" onClick={() => setHeadshot(DEFAULT_HEADSHOT)}>
                  Remove
                </button>
              )}
            </div>

            <div
              className={`lw-drop ${dragOver ? "is-over" : ""} ${headshot.src ? "has-image" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && fileInputRef.current && fileInputRef.current.click()}
            >
              {headshot.src ? (
                <>
                  <img src={headshot.src} alt="Uploaded headshot" />
                  <span className="lw-drop__change">Change photo</span>
                </>
              ) : (
                <>
                  <span className="lw-drop__icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <circle cx="9" cy="10" r="1.6" />
                      <path d="M21 16l-5-5-9 8" />
                    </svg>
                  </span>
                  <strong>Drop a headshot here</strong>
                  <span>or click to browse - JPG or PNG, square-ish works best</span>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  loadFile(e.target.files && e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>

            <div className={`lw-field lw-field--row ${headshot.src ? "" : "is-disabled"}`}>
              <label htmlFor="lw-zoom">Zoom</label>
              <input
                id="lw-zoom"
                type="range"
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.01}
                value={headshot.zoom}
                disabled={!headshot.src}
                onChange={(e) => setHeadshot((h) => ({ ...h, zoom: Number(e.target.value) }))}
              />
              <button
                type="button"
                className="lw-link"
                disabled={!headshot.src}
                onClick={() => setHeadshot((h) => ({ ...h, zoom: 1, x: 0, y: 0 }))}
              >
                Reset
              </button>
            </div>
            <p className="lw-hint">Drag the preview to reposition the photo.</p>
          </section>

          {/* Details */}
          <section className="lw-section">
            <div className="lw-section__head">
              <h2>Details</h2>
            </div>
            <div className="lw-field">
              <label htmlFor="lw-name">Full name</label>
              <input id="lw-name" type="text" placeholder="Kennedy Smith" value={data.name} onChange={update("name")} />
            </div>
            <div className="lw-field">
              <label htmlFor="lw-role">Role / title</label>
              <input id="lw-role" type="text" placeholder="Business Development Associate" value={data.role} onChange={update("role")} />
            </div>
            <div className="lw-field">
              <label htmlFor="lw-team">Team badge</label>
              <input id="lw-team" type="text" placeholder="Ignite Team" value={data.team} onChange={update("team")} />
            </div>
            <div className="lw-field">
              <label htmlFor="lw-tagline">Tagline</label>
              <input id="lw-tagline" type="text" placeholder="Welcome to the family" value={data.tagline} onChange={update("tagline")} />
            </div>
            <div className="lw-field">
              <label htmlFor="lw-note">Handwritten note</label>
              <input id="lw-note" type="text" placeholder="Glad you're here!" value={data.note} onChange={update("note")} />
            </div>
          </section>

          {/* Templates */}
          <section className="lw-section">
            <div className="lw-section__head">
              <h2>Template</h2>
              <span className="lw-section__meta">{TEMPLATES.findIndex((t) => t.id === templateId) + 1} / {TEMPLATES.length}</span>
            </div>
            <div className="lw-thumbs">
              {TEMPLATES.map((t) => {
                const Thumb = t.Component;
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={`lw-thumb ${t.id === templateId ? "is-active" : ""}`}
                    onClick={() => setTemplateId(t.id)}
                    title={t.description}
                  >
                    <ScaledCanvas className="lw-thumb__canvas">
                      <Thumb {...templateProps} />
                    </ScaledCanvas>
                    <span className="lw-thumb__label">{t.name}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>

        <main className="lw-stage">
          <div className="lw-stage__head">
            <div>
              <h2>{template.name}</h2>
              <p>{template.description}</p>
            </div>
            <span className="lw-chip">1080 x 1080</span>
          </div>

          <ScaledCanvas
            className={`lw-preview ${headshot.src ? "is-draggable" : ""}`}
            canvasRef={previewRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <Active {...templateProps} />
          </ScaledCanvas>

          <div className="lw-stage__nav">
            <button
              type="button"
              className="lw-btn lw-btn--ghost"
              onClick={() => setTemplateId(TEMPLATES[(TEMPLATES.findIndex((t) => t.id === templateId) + TEMPLATES.length - 1) % TEMPLATES.length].id)}
            >
              Previous style
            </button>
            <button
              type="button"
              className="lw-btn lw-btn--ghost"
              onClick={() => setTemplateId(TEMPLATES[(TEMPLATES.findIndex((t) => t.id === templateId) + 1) % TEMPLATES.length].id)}
            >
              Next style
            </button>
          </div>
        </main>
      </div>

      {/* Native-size renders used for export only. */}
      <div className="lw-export-stage" aria-hidden="true">
        {TEMPLATES.map((t) => {
          const Comp = t.Component;
          return (
            <div
              key={t.id}
              ref={(el) => {
                exportRefs.current[t.id] = el;
              }}
            >
              <Comp {...templateProps} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}
